import Icon from "components/Common/Icon";
import { getWowStatic } from "hooks/wow/getStaticData";
import { getEnchantName, getEnchantQuality, getEnchantQualityIconSrc, getEnchantImgLinkSrc, trimQuality } from "lib/enchant-ids";
import React, { useEffect } from "react";
import WowTooltip from "../../Tooltips/WowTooltip";
import SubtitleAndDescription from "../../Common/SubtitleAndDescription";
import classnames from "classnames";
import { useQueryString } from "hooks/general-hooks";

const ProbuildsGearGuide = (props) => {
  const queryParams = useQueryString();
  const usingArmoryData = !queryParams["encounter"];

  return (
    <div className="wow-items-section content-section probuilds-armory">
      <div className="header-section">
        <div className="title-and-button">
          <SubtitleAndDescription subtitle="Armory" />
        </div>
      </div>
      <WOWArmorLayoutContainer {...props} usingArmoryData={usingArmoryData} />
      <WOWWeaponLayoutContainer {...props} usingArmoryData={usingArmoryData} />
    </div>
  );
};

const WOWArmorLayoutContainer = (props) => {
  const firstColumnItems = ["head", "neck", "shoulder", "back", "chest", "shirt", "tabard", "wrist"];
  const secondColumnItems = ["hands", "waist", "legs", "feet", "finger_1", "finger_2", "trinket_1", "trinket_2"];

  return (
    <div className="armor-layout-container">
      <WOWArmorLayoutColumn {...props} itemSlots={firstColumnItems} isLeftSide />
      <WOWArmorLayoutColumn {...props} itemSlots={secondColumnItems} />
    </div>
  );
};

const WOWArmorLayoutColumn = (props) => {
  const { builds, itemSlots, isLeftSide, usingArmoryData } = props;

  const itemData = itemSlots.map((slot) => {
    if (!builds) {
      return {
        slot,
      };
    }

    const itemInSlot = builds.filter((item) => item.slot.toLowerCase() === slot)[0];

    if (itemInSlot) {
      return {
        id: itemInSlot.item,
        slot,
        enchant: usingArmoryData ? itemInSlot.enchant_name[0] : itemInSlot.enchant_name,
        itemLevel: itemInSlot.item_level,
        gems: itemInSlot.gems ?? [],
        quality: itemInSlot.quality,
      };
    }

    return {
      slot,
    };
  });

  return (
    <div className="armor-layout">
      {itemData.map((item, index) => {
        return (
          <ArmorItem
            key={index}
            id={item.id}
            isLeftSide={isLeftSide}
            // itemColor={getItemQualityColor(item.quality)}
            itemColor={item.quality === 5 ? "#ff8000" : "rgb(163, 53, 238)"}
            itemLevel={item.itemLevel}
            enchantName={item.enchant}
            gems={item.gems}
            slot={item.slot}
            {...props}
          />
        );
      })}
    </div>
  );
};

const ArmorItem = (props) => {
  const { isLeftSide, id, enchantName, slot, itemLevel, gems } = props;
  let { itemName = "Cosmetic", itemColor, className } = props;

  const staticData = getWowStatic("item", id);
  let img;
  if (!staticData || !staticData.name) {
    if (slot === "shirt" || slot === "tabard") {
      img = `https://static.bigbrain.gg/wow/icons/empty-slots/${slot}.png`;
    } else {
      itemName = "";
    }

    itemColor = "#717171";
    className = "empty-slot";
  } else {
    itemName = staticData.name;
    img = staticData.img;
  }

  return (
    <div className={isLeftSide ? `armor-item ${className}` : `armor-item-reversed ${className}`} item-id={id}>
      <div className="armor-item-container">
        <WowTooltip type="item" id={id}>
          <Icon staticDataImg={img} color={itemColor} classType="bis-item-icon" />
        </WowTooltip>
        <div className={isLeftSide ? "item-info" : "item-info-right"}>
          <div className="item-name" style={{ color: itemColor }}>
            {itemName}
          </div>
          <div
            className={classnames("enchant-info", {
              right: !isLeftSide,
            })}
          >
            <div className="enchant-text item-level">{itemLevel}</div>
            {gems?.length > 0 && (
              <div className="gems-container">
                {gems.map((data, index) => (
                  <Gem data={data} key={index} />
                ))}
              </div>
            )}
            <div className="enchant-info-inner-container">
              {enchantName ? <Icon iconSize="18px" staticDataImg={getEnchantImgLinkSrc(enchantName)} /> : null}
              <div className="enchant-text">{trimQuality(getEnchantName(enchantName?.split(" (")[0]))}</div>
              {enchantName ? (
                <img
                  style={
                    getEnchantQuality(enchantName) == 2
                      ? { width: "16px", height: "8px", marginLeft: "4px" }
                      : { width: "16px", height: "12px", marginLeft: "4px" }
                  }
                  src={getEnchantQualityIconSrc(enchantName)}
                ></img>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Gem = ({ data }) => {
  const staticData = getWowStatic("item", data);

  return staticData ? (
    <WowTooltip type="item" id={data}>
      <Icon staticDataImg={staticData.img} iconSize="16px" />
    </WowTooltip>
  ) : null;
};

const WOWWeaponLayoutContainer = (props) => {
  const { builds, usingArmoryData } = props;

  const weapon1 = builds?.filter((item) => item.slot.toLowerCase() === "main_hand")[0];
  const weapon2 = builds?.filter((item) => item.slot.toLowerCase() === "off_hand")[0];
  const weapon1Data = getWowStatic("item", weapon1?.item);
  const weapon2Data = getWowStatic("item", weapon2?.item); // const weapon2Img = weaponSlot2?.img;

  useEffect(() => {
    if (window && typeof window.CurseTips !== "undefined") {
      CurseTips["wowdb-tooltip"].watchElements(document.getElementsByClassName("tooltip-trigger"));
    }
  }, [weapon1Data, weapon2Data]);

  if (!weapon2Data) {
    return (
      <>
        <div className="weapon-combos-label">Weapon: Main Hand</div>
        <div className="weapon-layout-container">
          <div className="weapons">
            <ArmorItem
              isLeftSide
              itemName={weapon1Data?.name}
              // itemColor={getItemQualityColor(weaponSlot1.quality)}
              itemColor={weapon1.quality === 5 ? "#ff8000" : "rgb(163, 53, 238)"}
              itemLevel={weapon1?.item_level}
              img={weapon1Data?.img}
              id={weapon1?.item}
              enchantName={usingArmoryData ? weapon1?.enchant_name[0] : weapon1?.enchant_name}
            />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="weapon-combos-label">Weapons: Main Hand & Off Hand</div>
      <div className="weapon-layout-container weapon-combos">
        <div className="weapons">
          <ArmorItem
            isLeftSide
            itemName={weapon1Data?.name}
            // itemColor={getItemQualityColor(weaponSlot1.quality)}
            itemColor={weapon1.quality === 5 ? "#ff8000" : "rgb(163, 53, 238)"}
            itemLevel={weapon1.item_level}
            img={weapon1Data?.img}
            id={weapon1.item}
            enchantName={usingArmoryData ? weapon1.enchant_name[0] : weapon1.enchant_name}
          />
          <ArmorItem
            itemName={weapon2Data?.name}
            // itemColor={getItemQualityColor(weaponSlot1.quality)}
            itemColor={weapon2.quality === 5 ? "#ff8000" : "rgb(163, 53, 238)"}
            itemLevel={weapon2.item_level}
            img={weapon2Data?.img}
            id={weapon2.item}
            enchantName={usingArmoryData ? weapon2.enchant_name[0] : weapon2.enchant_name}
          />
        </div>
      </div>
    </>
  );
};

export default ProbuildsGearGuide;
